<template>
  <AuthGuard>
    <v-container class="app-page-content pa-4 pa-md-6">
      <v-tooltip v-if="canCreateCustomers" text="Add a new customer" location="left">
        <template #activator="{ props }">
          <v-btn
            v-bind="props"
            icon
            color="primary"
            class="fab"
            @click="state.isCreateCustomerDialogActive = true"
          >
            <v-icon>mdi-plus</v-icon>
          </v-btn>
        </template>
      </v-tooltip>

      <div class="d-flex flex-wrap justify-space-between">
        <div class="text-h4 font-weight-bold mb-6">Customers</div>
        <div class="mb-4">
          <v-tooltip :text="reportListTooltip" location="bottom">
            <template #activator="{ props }">
              <v-btn
                class="text-transform-none mb-2 mr-0 mr-sm-1 w-100 w-sm-auto"
                v-bind="props"
                :to="canListReports ? URLs.PAGE_REPORT_LIST : undefined"
                variant="flat"
                :disabled="!canListReports"
                @click="handleClickViewReports"
              >
                View Reports
              </v-btn>
            </template>
          </v-tooltip>

          <v-tooltip :text="reportBuildTooltip" location="bottom">
            <template #activator="{ props }">
              <v-btn
                v-bind="props"
                color="primary"
                class="text-transform-none mb-2 mr-0 mr-sm-1 w-100 w-sm-auto"
                @click="handleClickMultiView"
                :disabled="
                  !canBuildReports ||
                  state.selectedCustomers.length === 0 ||
                  state.selectedCustomers.length > 1
                "
              >
                Build Report
              </v-btn>
            </template>
          </v-tooltip>

          <v-tooltip
            :text="
              !canBuildReports
                ? reportBuildPermissionMessage
                : state.selectedCustomers.length === 0
                  ? 'Select one or more customers'
                  : !selectedCustomersAllHaveDataSources
                    ? 'Every selected customer must have at least one data source'
                    : 'Generate reports for selected customers. You can still edit them later.'
            "
            location="bottom"
          >
            <template #activator="{ props }">
              <v-btn
                v-bind="props"
                color="primary"
                class="text-transform-none mb-2 mr-0 mr-sm-1 w-100 w-sm-auto"
                @click="handleClickGenerateReport"
                :disabled="
                  !canBuildReports ||
                  state.selectedCustomers.length === 0 ||
                  !selectedCustomersAllHaveDataSources
                "
              >
                <span v-if="state.selectedCustomers.length === 1">Generate Report</span>
                <span v-else>Generate Reports</span>
              </v-btn>
            </template>
          </v-tooltip>

          <v-tooltip v-if="canDeleteCustomers" text="Delete selected customers" location="bottom">
            <template #activator="{ props }">
              <v-btn
                v-bind="props"
                color="error"
                variant="outlined"
                class="text-transform-none mb-2 mr-0 mr-sm-1 w-100 w-sm-auto"
                @click="state.isDeleteConfirmDialogActive = true"
                :disabled="state.selectedCustomers.length === 0 || state.isDeleteLoading"
                :loading="state.isDeleteLoading"
              >
                Delete
              </v-btn>
            </template>
          </v-tooltip>
        </div>
      </div>

      <v-alert v-if="!canListCustomers" type="error" variant="tonal" class="mb-6">
        {{ customerListPermissionMessage }}
      </v-alert>

      <v-alert v-if="!canBuildReports" type="error" variant="tonal" class="mb-6">
        {{ reportBuildPermissionMessage }}
      </v-alert>

      <v-card class="mb-6 pa-4" elevation="2" variant="text">
        <v-row>
          <v-col cols="12" v-if="state.fetchCustomersLoading">
            <v-skeleton-loader type="list-item" />
            <v-skeleton-loader type="list-item" />
          </v-col>

          <v-col cols="12" v-else>
            <v-data-table
              :headers="customersTable.headers"
              :items="customersTable.items"
              item-value="id"
              :loading="state.fetchCustomersLoading"
              density="comfortable"
              :sort-by="[{ key: 'name', order: 'asc' }]"
              items-per-page="50"
              v-model="state.selectedCustomers"
              v-model:search="state.customersSearch"
              show-select
              class="bg-transparent"
            >
              <template #top>
                <v-text-field
                  v-model="state.customersSearch"
                  label="Search Customers"
                  prepend-inner-icon="mdi-magnify"
                  variant="solo-filled"
                  flat
                  hide-details
                  dense
                  class="mx-2 my-2"
                />
              </template>
              <template v-slot:item.edit="{ item }">
                <div class="d-flex">
                  <v-tooltip text="Edit Customer Settings" location="top">
                    <template v-slot:activator="{ props }">
                      <v-btn
                        v-bind="props"
                        icon="mdi-cog"
                        variant="flat"
                        @click="router.push(URLs.PAGE_CUSTOMER_EDIT(item.id))"
                        size="30"
                      ></v-btn>
                    </template>
                  </v-tooltip>
                </div>
              </template>
              <template v-slot:item.name="{ item }">
                <span class="text-decoration-none" style="color: black">
                  {{ item.name }}
                </span>
              </template>
              <template v-slot:item.gsc="{ item }">
                <v-tooltip :text="getAccountCellTooltip(item.gsc)" location="top">
                  <template #activator="{ props }">
                    <span v-bind="props" :style="getAccountCellStyle(item.gsc)">
                      <template v-if="item.gsc">
                        {{ item.gsc.name
                        }}<span v-if="isAccountPaused(item.gsc)" class="ml-1 text-caption"
                          >(Paused)</span
                        >
                      </template>
                      <template v-else>—</template>
                    </span>
                  </template>
                </v-tooltip>
              </template>
              <template v-slot:item.ga="{ item }">
                <v-tooltip :text="getAccountCellTooltip(item.ga)" location="top">
                  <template #activator="{ props }">
                    <span v-bind="props" :style="getAccountCellStyle(item.ga)">
                      <template v-if="item.ga">
                        {{ item.ga.name
                        }}<span v-if="isAccountPaused(item.ga)" class="ml-1 text-caption"
                          >(Paused)</span
                        >
                      </template>
                      <template v-else>—</template>
                    </span>
                  </template>
                </v-tooltip>
              </template>
              <template v-slot:item.be="{ item }">
                <v-tooltip :text="getAccountCellTooltip(item.be)" location="top">
                  <template #activator="{ props }">
                    <span v-bind="props" :style="getAccountCellStyle(item.be)">
                      <template v-if="item.be">
                        {{ item.be.name
                        }}<span v-if="isAccountPaused(item.be)" class="ml-1 text-caption"
                          >(Paused)</span
                        >
                      </template>
                      <template v-else>—</template>
                    </span>
                  </template>
                </v-tooltip>
              </template>
              <template v-slot:item.createdAt="{ item }">
                <span class="text-no-wrap">{{
                  moment(item.createdAt).format(Constants.HUMAN_DATE_FORMAT_SHORT)
                }}</span>
              </template>
            </v-data-table>
          </v-col>
        </v-row>
      </v-card>
      <v-dialog v-model="state.isDeleteConfirmDialogActive" max-width="500" persistent>
        <template v-slot:default>
          <v-card>
            <v-card-title>Delete Customers</v-card-title>
            <v-card-text>
              Are you sure you want to delete
              {{
                state.selectedCustomers.length === 1
                  ? "this customer"
                  : `these ${state.selectedCustomers.length} customers`
              }}? This action cannot be undone.
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn
                text="Cancel"
                @click="state.isDeleteConfirmDialogActive = false"
                :disabled="state.isDeleteLoading"
              />
              <v-btn
                color="error"
                text="Delete"
                @click="handleDeleteCustomers"
                :loading="state.isDeleteLoading"
              />
            </v-card-actions>
          </v-card>
        </template>
      </v-dialog>
      <v-dialog
        v-model="state.isCreateCustomerDialogActive"
        max-width="480"
        scrollable
        content-class="customer-dialog-overlay"
      >
        <v-card rounded="lg" elevation="12" class="customer-dialog-card">
          <v-card-title class="d-flex align-center justify-space-between px-5 pt-5 pb-2">
            <span class="text-h6 font-weight-bold">Add new customer</span>
            <v-btn
              icon="mdi-close"
              variant="text"
              size="small"
              density="comfortable"
              aria-label="Close"
              @click="state.isCreateCustomerDialogActive = false"
            />
          </v-card-title>
          <v-divider class="mx-0" />
          <v-card-text class="px-5 py-4">
            <CreateCustomerForm
              type="create"
              :customer="{ name: '' }"
              @cancel="state.isCreateCustomerDialogActive = false"
            />
          </v-card-text>
        </v-card>
      </v-dialog>
      <v-dialog v-model="state.isGenerateReportDialogActive" max-width="800">
        <template v-slot:default>
          <v-card>
            <v-card-title>Generate Report</v-card-title>
            <v-card-text>
              <div class="d-flex justify-space-between align-center">
                <div class="d-flex flex-column">
                  <span class="font-weight-bold">Select Months to Compare</span>
                  <v-btn-toggle
                    v-model="state.numberOfMonths"
                    class="mb-3 btn-range-selector"
                    mandatory
                  >
                    <v-btn
                      v-for="i in [3, 6, 12]"
                      @click="handleClickSelectedMonths(i)"
                      :class="{ selected: state.numberOfMonths === i }"
                      :key="i"
                      class="text-transform-none"
                    >
                      {{ i }} Months
                    </v-btn>
                  </v-btn-toggle>
                </div>
              </div>

              <div class="month-range-selector">
                <div class="month-picker">
                  <div :style="{ color: Colors.Green }">
                    <span class="d-inline-block">Current Period</span>
                    <span class="text-no-wrap"
                      >({{ moment(_.head(months.selectedMonths), "YYYY-MM").format("MMM YYYY") }} -
                      {{
                        moment(_.last(months.selectedMonths), "YYYY-MM").format("MMM YYYY")
                      }})</span
                    >
                  </div>
                  <MonthRangePicker
                    :numberOfMonths="state.numberOfMonths"
                    v-model="months.selectedMonths"
                    :activeColor="Colors.Green"
                    @on-scroll-change="state.comparedScrollPosition = $event"
                    :scroll-position="-1"
                  />
                </div>
                <div class="month-picker mt-4">
                  <div :style="{ color: Colors.Blue }">
                    <span class="d-inline-block">Previous Period</span>
                    <span class="text-no-wrap"
                      >({{ moment(_.head(months.comparedMonths), "YYYY-MM").format("MMM YYYY") }} -
                      {{
                        moment(_.last(months.comparedMonths), "YYYY-MM").format("MMM YYYY")
                      }})</span
                    >
                  </div>
                  <MonthRangePicker
                    :numberOfMonths="state.numberOfMonths"
                    v-model="months.comparedMonths"
                    :scroll-position="state.comparedScrollPosition"
                    :disabled-months="[
                      ...months.selectedMonths,
                      ...Array.from(
                        iterateMonths(
                          moment(_.last(months.selectedMonths), 'YYYY-MM')
                            .add(1, 'months')
                            .format('YYYY-MM'),
                          moment(_.last(months.selectedMonths), 'YYYY-MM')
                            .add(state.numberOfMonths, 'months')
                            .format('YYYY-MM')
                        )
                      )
                    ]"
                  />
                </div>
              </div>

              <div v-if="canGenerateReportSummary" class="mt-4">
                <v-checkbox
                  label="Include AI Summary"
                  v-model="state.includeAISummary"
                  hide-details
                ></v-checkbox>
                <v-autocomplete
                  v-show="state.includeAISummary"
                  label="Choose the prompt used to generate the AI summary."
                  :items="prompts"
                  item-title="title"
                  item-value="id"
                  variant="filled"
                  v-model="state.selectedPrompt"
                ></v-autocomplete>
              </div>

              <div
                class="align-center d-flex justify-end flex-column-reverse flex-sm-row flex-wrap mt-4"
              >
                <v-btn
                  @click="state.isGenerateReportDialogActive = false"
                  class="mt-2 mt-sm-0 w-100 w-sm-auto"
                  :disabled="state.isReportGenerateLoading"
                  :loading="state.isReportGenerateLoading"
                  text="Cancel"
                />
                <v-btn
                  @click="handleSubmitGenerateReport"
                  color="primary"
                  class="ml-0 ml-sm-2 w-100 w-sm-auto"
                  :disabled="
                    months.selectedMonths.length === 0 ||
                    months.comparedMonths.length !== months.selectedMonths.length ||
                    state.isReportGenerateLoading ||
                    !selectedCustomersAllHaveDataSources
                  "
                  :loading="state.isReportGenerateLoading"
                >
                  Generate Report
                </v-btn>
              </div>
            </v-card-text>
          </v-card>
        </template>
      </v-dialog>
    </v-container>
  </AuthGuard>
</template>
<script setup lang="ts">
import Report from "@/actions/Report";
import AuthGuard from "@/components/Auth/AuthGuard.vue";
import CreateCustomerForm from "@/components/Customer/CreateCustomerForm.vue";
import MonthRangePicker from "@/components/Widgets/MonthRangePicker.vue";
import { Colors, Constants, URLs } from "@/constants";
import usePrompt from "@/hooks/usePrompt";
import { useAuthStore } from "@/stores/auth";
import { useCustomerStore } from "@/stores/customer";
import { IApp } from "@/types";
import Auth from "@/utils/auth";
import {
  flatten,
  isCustomerValid,
  iterateMonths,
  makeAlert,
  customerHasAnyDataSource
} from "@/utils";
import _ from "lodash";
import moment from "moment";
import { computed, onMounted, reactive, toRefs, watch } from "vue";
import { useRouter } from "vue-router";

const state = reactive<{
  fetchCustomersLoading: boolean;
  isCreateCustomerDialogActive: boolean;
  customer?: IApp.ICustomer;
  customersSearch: string;
  selectedCustomers: string[];
  isGenerateReportDialogActive: boolean;
  numberOfMonths: number;
  isReportGenerateLoading: boolean;
  comparedScrollPosition: number;
  includeAISummary: boolean;
  selectedPrompt: string | null;
  isDeleteConfirmDialogActive: boolean;
  isDeleteLoading: boolean;
}>({
  fetchCustomersLoading: false,
  isCreateCustomerDialogActive: false,
  customersSearch: "",
  selectedCustomers: [],
  isGenerateReportDialogActive: false,
  numberOfMonths: 0,
  isReportGenerateLoading: false,
  comparedScrollPosition: -1,
  includeAISummary: true,
  selectedPrompt: null,
  isDeleteConfirmDialogActive: false,
  isDeleteLoading: false
});

const { numberOfMonths } = toRefs(state);

const months = reactive<{
  selectedMonths: string[];
  comparedMonths: string[];
}>({
  selectedMonths: [],
  comparedMonths: []
});

const authStore = useAuthStore();
const customerStore = useCustomerStore();
const router = useRouter();
const { prompts } = usePrompt();

const canDeleteCustomers = computed(() => authStore.hasPermission(Auth.Permission.CUSTOMER_DELETE));
const canCreateCustomers = computed(() => authStore.hasPermission(Auth.Permission.CUSTOMER_CREATE));
const canListCustomers = computed(() => authStore.hasPermission(Auth.Permission.CUSTOMER_LIST));
const canBuildReports = computed(() => authStore.hasPermission(Auth.Permission.REPORT_CREATE));
const canListReports = computed(() => authStore.hasPermission(Auth.Permission.REPORT_LIST));
const canGenerateReportSummary = computed(() =>
  authStore.hasPermission(Auth.Permission.REPORT_SUMMARY_GENERATE)
);
const customerListPermissionMessage = "You don't have permission to see the customer list";
const reportBuildPermissionMessage = "You don't have permission to build report";
const reportListPermissionMessage = "You don't have permission to view reports";
const reportBuildTooltip = computed(() =>
  canBuildReports.value
    ? "Open a report draft to review and edit before creating."
    : reportBuildPermissionMessage
);
const reportListTooltip = computed(() =>
  canListReports.value ? "View reports for all customers." : reportListPermissionMessage
);

const customers = computed(() => _.filter(customerStore.customers, isCustomerValid));

const selectedCustomersAllHaveDataSources = computed(() => {
  return state.selectedCustomers.every((id) => {
    const customer = customerStore.getCustomer(id);
    return !!customer && customerHasAnyDataSource(customer);
  });
});

function pickFeedAccount(
  accounts: IApp.IAccount[] | undefined,
  feed: IApp.IFeedTypes
): IApp.IAccount | undefined {
  if (!accounts?.length) return undefined;
  const active = _.find(accounts, { feed, status: IApp.IAccountStatus.Active });
  if (active) return active;
  return _.find(accounts, { feed, status: IApp.IAccountStatus.Paused });
}

function isAccountPaused(account: IApp.IAccount | undefined): boolean {
  return account?.status === IApp.IAccountStatus.Paused;
}

const customersTable = computed(() => {
  return {
    items: _(customers.value)
      .map((item, index) => ({
        no: index + 1,
        id: item.id,
        name: item.name,
        gsc: pickFeedAccount(item.accounts, IApp.IFeedTypes.GoogleSearchConsole),
        ga: pickFeedAccount(item.accounts, IApp.IFeedTypes.GoogleAnalytics),
        be: pickFeedAccount(item.accounts, IApp.IFeedTypes.BirdEye),
        createdAt: item.createdAt,
        edit: ""
      }))
      .value(),
    headers: [
      {
        title: "Name",
        key: "name",
        sortable: true
      },
      {
        title: "Google Search Console",
        key: "gsc",
        sortable: true
      },
      {
        title: "Google Analytics",
        key: "ga",
        sortable: true
      },
      {
        title: "Bird Eye",
        key: "be",
        sortable: true
      },
      {
        title: "Created At",
        key: "createdAt",
        width: "200px",
        sortable: true,
        sort: (a: string, b: string) => {
          return new Date(a).getTime() - new Date(b).getTime();
        }
      },
      { title: "Edit", key: "edit", width: "40px" }
    ] as IApp.DataTableHeader[],
    filter(value: string, query: string) {
      if (!_.isString(value)) {
        return false;
      }
      return _.indexOf(value.toLowerCase(), query?.toLowerCase()) != -1;
    }
  };
});

function getAccountCellTooltip(account: IApp.IAccount | undefined): string {
  if (!account) return "Account does not exist";
  if (account.status === IApp.IAccountStatus.Paused) {
    const syncTail = account.isSynced
      ? " Synced."
      : typeof account.syncedPercent === "number"
        ? ` Not synced. Synced: ${account.syncedPercent}%.`
        : " Not synced.";
    return `Paused — automated sync/fetch is stopped.${syncTail}`;
  }
  if (account.isSynced) return "Synced";
  const pct = account.syncedPercent;
  return typeof pct === "number" ? `Not synced. Synced: ${pct}%` : "Not synced";
}

function getAccountCellStyle(account: IApp.IAccount | undefined): string {
  if (!account) return "";
  if (account.status === IApp.IAccountStatus.Paused) {
    return "color: rgb(245, 124, 0);";
  }
  if (account.isSynced) return "color: rgb(34, 139, 34);";
  return "color: rgb(220, 53, 69);";
}

const handleClickMultiView = () => {
  if (!canBuildReports.value) {
    return;
  }
  if (state.selectedCustomers.length === 0) {
    makeAlert("Please select at least one customer", "error");
    return;
  }
  router.push({
    path: URLs.PAGE_MULTIPLE_CUSTOMER_DASHBOARD,
    query: flatten({
      "customer-id": state.selectedCustomers,
      mode: IApp.PrintMode.Edit,
      lazy: true
    })
  });
};

const handleClickViewReports = () => {
  if (!canListReports.value) {
    makeAlert(reportListPermissionMessage, "error");
  }
};

const handleClickSelectedMonths = (interval: number) => {
  state.numberOfMonths = interval;
};

const handleClickGenerateReport = () => {
  if (!canBuildReports.value) {
    return;
  }
  if (state.selectedCustomers.length === 0) {
    return;
  }
  if (!selectedCustomersAllHaveDataSources.value) {
    makeAlert(
      "Every selected customer must have at least one data source before you can generate a report.",
      "error"
    );
    return;
  }
  state.isGenerateReportDialogActive = true;
};

const handleDeleteCustomers = async () => {
  const userId = authStore.user?.id;
  if (!userId) {
    makeAlert("You must be logged in to delete customers", "error");
    return;
  }
  state.isDeleteLoading = true;
  const toDelete = [...state.selectedCustomers];
  let successCount = 0;
  let failedCount = 0;
  for (const customerId of toDelete) {
    const response = await customerStore.deleteCustomer(userId, customerId);
    if (response.status) {
      successCount++;
    } else {
      failedCount++;
      makeAlert(response.message ?? "Failed to delete customer", "error");
    }
  }
  state.selectedCustomers = [];
  state.isDeleteConfirmDialogActive = false;
  if (successCount > 0) {
    makeAlert(
      `${successCount} customer${successCount > 1 ? "s" : ""} deleted successfully`,
      failedCount > 0 ? "info" : "success"
    );
  }
  state.isDeleteLoading = false;
};

const handleSubmitGenerateReport = async () => {
  if (!canBuildReports.value) {
    return;
  }
  if (!selectedCustomersAllHaveDataSources.value) {
    makeAlert(
      "Every selected customer must have at least one data source before you can generate a report.",
      "error"
    );
    return;
  }
  if (!_.isEmpty(_.intersection(months.selectedMonths, months.comparedMonths))) {
    makeAlert("Selected months and compared months cannot overlap", "error");
    return;
  }
  state.isReportGenerateLoading = true;
  const response = await Report.create({
    dateRange: {
      start: _.head(months.selectedMonths)!,
      end: _.last(months.selectedMonths)!
    },
    comparedRange: {
      start: _.head(months.comparedMonths)!,
      end: _.last(months.comparedMonths)!
    },
    customerIds: state.selectedCustomers,
    showSummary: canGenerateReportSummary.value && state.includeAISummary,
    promptId: state.selectedPrompt === "default" ? null : state.selectedPrompt
  });

  if (response.status) {
    makeAlert(response.data.message, "success");
    state.isGenerateReportDialogActive = false;
    router.push({
      path: URLs.PAGE_REPORT_LIST
    });
  } else {
    makeAlert(response.message, "error");
  }
  state.isReportGenerateLoading = false;
};
watch(numberOfMonths, (newValue, oldValue) => {
  if (newValue !== oldValue) {
    months.selectedMonths = Array.from(
      iterateMonths(
        moment().subtract(newValue, "months").format("YYYY-MM"),
        moment().subtract(1, "months").format("YYYY-MM")
      )
    );
    months.comparedMonths = Array.from(
      iterateMonths(
        moment()
          .subtract(newValue * 2, "months")
          .format("YYYY-MM"),
        moment()
          .subtract(newValue + 1, "months")
          .format("YYYY-MM")
      )
    );
  }
});

onMounted(() => {
  setTimeout(() => {
    state.numberOfMonths = 3;
  }, 100);
});

watch(
  [() => prompts.value, () => state.selectedPrompt],
  () => {
    if (state.selectedPrompt === null && prompts.value.length > 0) {
      state.selectedPrompt = _.head(prompts.value)?.id ?? null;
    }
  },
  { immediate: true }
);
</script>

<style scoped>
.selected {
  background-color: rgba(0, 123, 255, 0.5) !important;
  color: white !important;
  border-radius: 5px;
}
.fab {
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 100;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.3);
}
.v-table :deep(.v-data-table-header__content > span) {
  font-weight: bold;
}
:deep(.customer-dialog-overlay) {
  backdrop-filter: blur(2px);
}
.customer-dialog-card {
  border: 1px solid rgba(0, 0, 0, 0.08);
}
</style>
